export type ManagedUser = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "custom";
  permissions: string[];
  active: boolean;
  mustChangePassword: boolean;
  sessionVersion: number;
  lastLoginAt: string;
  notes: string;
  updatedAt: string;
};

const BASE = String(import.meta.env.VITE_API_BASE_URL || "/api").replace(/\/$/, "");

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    ...init,
    headers: { Accept: "application/json", ...(init?.body ? { "Content-Type": "application/json" } : {}), ...init?.headers },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = typeof body?.detail === "string" ? body.detail : body?.detail?.message;
    throw new Error(detail || `HTTP ${response.status}`);
  }
  return body as T;
}

export const accountApi = {
  list: () => request<{ users: ManagedUser[]; permissions: string[] }>("/admin/users"),
  create: (payload: { email: string; name: string; role: "admin" | "custom"; permissions: string[]; temporaryPassword: string; notes: string }) =>
    request<{ user: ManagedUser }>("/admin/users", { method: "POST", body: JSON.stringify(payload) }),
  update: (userId: string, payload: { email: string; name: string; role: "admin" | "custom"; permissions: string[]; active: boolean; notes: string }) =>
    request<{ user: ManagedUser }>(`/admin/users/${encodeURIComponent(userId)}`, { method: "PUT", body: JSON.stringify(payload) }),
  delete: (userId: string) =>
    request<{ ok: boolean; userId: string }>(`/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" }),
  resetPassword: (userId: string, temporaryPassword: string) =>
    request<{ user: ManagedUser }>(`/admin/users/${encodeURIComponent(userId)}/reset-password`, { method: "POST", body: JSON.stringify({ temporaryPassword }) }),
  revokeSessions: (userId: string) =>
    request<{ user: ManagedUser }>(`/admin/users/${encodeURIComponent(userId)}/revoke-sessions`, { method: "POST" }),
};
